// pages/Offers.tsx
import React from "react";
import Button from "../components/Button";
import ConfirmationDialog from "../components/ConfirmationDialog";
import DataTableView from "../components/DataTableView2";
import DatePicker from "../components/DatePicker";
import AddOfferModal from "../components/offer/AddOfferModal";
import EditOfferModal from "../components/offer/EditOfferModal";
import Select from "../components/Select";
import Toggle from "../components/Toggle";
import { docsOffers } from "../utils/types";
import useOffers from '../hooks/logics/offers/use-offers-dashboard';
import { userTypes } from '../lib/constants/constant';

const Offers = () => {
  const {
    offers, totalPages, isLoading, isDeletingOffer,
    currentPage, setCurrentPage,
    validFrom, validTo,
    isActive, setIsActive,
    userType, setUserType,
    selectedOffer, setSelectedOffer,
    isAddOfferModalVisible, setIsAddOfferModalVisible,
    isEditModalOpen, setIsEditModalOpen,
    isDeleteModalOpen, setIsDeleteModalOpen,
    offerStatusChangeId, isAnyFilterApplied,
    handleSearch, clearFilters,
    handleDeleteOffer, handleOfferStatusChange,
    handleFromDateChange, handleToDateChange,
    formatsDate, formatUserType, refetch,
  } = useOffers();

  const columns = [
    {
      header: "Credits",
      className: "md:p-0 md:pl-10",
      accessor: (row: docsOffers) => (
        <div className="md:!pl-7">{row.credits ? `$ ${row.credits}` : "N/A"}</div>
      ),
    },
    {
      header: "Type",
      accessor: (row: docsOffers) => row.userType ? formatUserType(row.userType) : "N/A",
    },
    {
      header: "Start date",
      accessor: (row: docsOffers) => row.startDate ? formatsDate(row.startDate) : "N/A",
    },
    {
      header: "Expiry date",
      accessor: (row: docsOffers) => row.expiryDate ? formatsDate(row.expiryDate) : "N/A",
    },
    {
      header: "Actions",
      accessor: (row: docsOffers) => (
        <div className="flex flex-row items-center space-x-2">
          <div className="ml-2">
            <Toggle
              labelClassName="mr-2"
              label="Active"
              disabled={offerStatusChangeId === row._id}
              checked={row.isOfferActive === true}
              onChange={() => handleOfferStatusChange(row._id, row.isOfferActive!)}
            />
          </div>
          <Button
            onClick={() => { setSelectedOffer(row); setIsEditModalOpen(true); }}
            className="text-primary w-max p-0"
            variant="ghost"
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            className="text-red-600 w-max p-0"
            onClick={() => { setSelectedOffer(row); setIsDeleteModalOpen(true); }}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="w-full mb-4 flex items-center justify-end gap-4 md:mt-0">
        <Button onClick={() => setIsAddOfferModalVisible(true)} className="w-full min-h-0 h-10 md:h-12 md:w-auto md:px-3">
          Add offer
        </Button>
      </div>

      <div className="w-full flex flex-wrap items-center justify-end gap-4 md:pb-4">
        <div className="w-full flex flex-row gap-x-2 md:gap-x-4 md:w-max mt-4 md:mt-0">
          <div className="flex flex-col items-start md:flex-row md:items-center gap-y-1 w-1/2 md:w-full">
            <label className="mr-2">From:</label>
            <DatePicker
              selected={validFrom ? new Date(validFrom) : null}
              onChange={handleFromDateChange}
              placeholderText="MM/DD/YYYY"
              className="min-h-0 h-8 md:h-10 py-1"
              inputClassName="py-0 md:py-2 h-8 md:h-10"
              popperClassName="!left-7"
            />
          </div>
          <div className="flex flex-col items-start md:flex-row md:items-center gap-y-1 w-1/2 md:w-full">
            <label className="mr-2">To:</label>
            <DatePicker
              selected={validTo ? new Date(validTo) : null}
              onChange={handleToDateChange}
              placeholderText="MM/DD/YYYY"
              className="min-h-0 h-8 md:h-10 py-1"
              inputClassName="py-0 md:py-2 h-8 md:h-10"
              popperClassName="!-left-7"
            />
          </div>
        </div>

        <div className="w-full md:w-auto flex gap-x-2 md:gap-x-4">
          <Select
            options={userTypes}
            placeholder="Select type"
            value={userType}
            onChange={(e) => setUserType(e.target.value)}
            className="md:w-32 outline-none bg-white rounded-lg min-h-0 h-8 md:h-10 pl-4 pr-2"
            containerClassName="w-1/2 md:w-auto"
          />
          <Select
            options={[{ label: "Active", value: "1" }, { label: "Inactive", value: "0" }]}
            placeholder="Status"
            value={isActive}
            onChange={(e) => setIsActive(e.target.value)}
            className="md:w-32 outline-none bg-white rounded-lg min-h-0 h-8 md:h-10 pl-4 pr-2"
            containerClassName="w-1/2 md:w-auto"
          />
        </div>

        <div className="w-full md:w-auto flex gap-x-2 md:gap-x-4 mb-8 md:mb-0">
          <Button disabled={!isAnyFilterApplied} onClick={handleSearch} className="flex-1 md:flex-none md:w-24 min-h-0 h-8 md:h-10">
            Apply
          </Button>
          <Button disabled={!isAnyFilterApplied} onClick={clearFilters} className="flex-1 md:flex-none md:w-24 min-h-0 h-8 md:h-10">
            Clear
          </Button>
        </div>
      </div>

      <DataTableView
        isFetching={isLoading}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        currentPage={currentPage}
        emptyStateMessage="No data found"
        columns={columns}
        data={offers}
      />

      <AddOfferModal
        onSuccess={refetch}
        isVisible={isAddOfferModalVisible}
        onHide={() => setIsAddOfferModalVisible(false)}
      />

      <EditOfferModal
        isVisible={isEditModalOpen}
        onHide={() => { setSelectedOffer(null); setIsEditModalOpen(false); }}
        onSuccess={refetch}
        offer={selectedOffer}
      />

      <ConfirmationDialog
        isVisible={isDeleteModalOpen}
        onHide={() => { setSelectedOffer(null); setIsDeleteModalOpen(false); }}
        isSubmitting={isDeletingOffer}
        isSubmitButtonDisabled={isDeletingOffer}
        handleClick={handleDeleteOffer}
        text="Are you sure you want to delete this offer?"
      />
    </>
  );
};

export default Offers;